import { useEffect, useState } from "react";
import axios from "axios";
import { UsersThree, ArrowClockwise, Check, X, Plus, Trash } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { TierBadge, fmtNum } from "@/components/TierBadge";

const STATUS_STYLE = { approved: "text-emerald-300 border-emerald-400/40", pending: "text-amber border-amber/40", rejected: "text-crimson border-crimson/40" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const cell = "w-full border border-white/15 bg-[#0A0A0A] px-2 py-1.5 font-mono text-xs text-white focus:border-violet-200 focus:outline-none";

const Totals = ({ t }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
    {[["Promoters", t.promoters, "ref-total-promoters"], ["Pending", t.pending, "ref-total-pending"], ["Uniques this month", fmtNum(t.month_uniques), "ref-total-uniques"], ["Est. payouts (month)", `$${t.estimated_payout.toFixed(2)}`, "ref-total-payout"]].map(([l, v, id]) => (
      <div key={id} data-testid={id} className="border border-white/10 bg-[#0A0A0A] p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{l}</p>
        <p className="mt-1 font-serif text-3xl text-violet-200">{v}</p>
      </div>
    ))}
  </div>
);

const TierEditor = ({ tiers, setTiers, onSave }) => {
  const set = (i, k, v) => setTiers(tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  return (
    <div data-testid="ref-tier-editor" className="mb-6 border border-white/10 bg-[#0A0A0A]/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">Commission ladder · marginal · monthly uniques</p>
        <div className="flex gap-2">
          {tiers.length < 6 && (
            <button data-testid="ref-tier-add" onClick={() => setTiers([...tiers, { name: `Tier ${tiers.length + 1}`, min_uniques: (tiers[tiers.length - 1]?.min_uniques || 0) + 10000, rate_per_1000: tiers[tiers.length - 1]?.rate_per_1000 || 25 }])} className="flex items-center gap-1 border border-white/15 px-2 py-1 font-mono text-[10px] uppercase text-white/60 hover:text-violet-200 transition-colors">
              <Plus size={12} /> Tier
            </button>
          )}
          <button data-testid="ref-rate-save" onClick={onSave} className="border border-violet-200/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-violet-200 hover:bg-violet-200/10 transition-colors">Save tiers</button>
        </div>
      </div>
      <div className="grid gap-2">
        {tiers.map((t, i) => (
          <div key={i} data-testid={`ref-tier-row-${i + 1}`} className="grid grid-cols-[1fr_1fr_1fr_32px] items-center gap-2">
            <input data-testid={`ref-tier-name-${i + 1}`} value={t.name} onChange={(e) => set(i, "name", e.target.value)} className={cell} />
            <label className="flex items-center gap-2 font-mono text-[10px] text-white/40">
              from
              <input data-testid={`ref-tier-min-${i + 1}`} type="number" min="0" step="1000" disabled={i === 0} value={t.min_uniques} onChange={(e) => set(i, "min_uniques", parseInt(e.target.value, 10) || 0)} className={`${cell} disabled:opacity-50`} />
            </label>
            <label className="flex items-center gap-2 font-mono text-[10px] text-white/40">
              $/1k
              <input data-testid={`ref-tier-rate-${i + 1}`} type="number" min="0" step="0.5" value={t.rate_per_1000} onChange={(e) => set(i, "rate_per_1000", parseFloat(e.target.value) || 0)} className={cell} />
            </label>
            {i > 0 ? (
              <button data-testid={`ref-tier-remove-${i + 1}`} onClick={() => setTiers(tiers.filter((_, j) => j !== i))} title="Remove tier" className="p-1.5 text-white/40 hover:text-crimson transition-colors"><Trash size={14} /></button>
            ) : <span />}
          </div>
        ))}
      </div>
    </div>
  );
};

const Row = ({ p, onStatus }) => (
  <tr data-testid={`ref-row-${p.promoter_id}`} className="border-b border-white/5 hover:bg-white/[0.02] text-sm">
    <td className="py-2.5 pr-4">
      <div className="flex items-center gap-2">
        <p className="text-white/85">@{p.promoter_id}</p>
        <TierBadge testid={`ref-tier-${p.promoter_id}`} name={p.tier} level={p.tier_level} />
      </div>
      <p className="font-mono text-[10px] text-white/35 truncate max-w-[240px]">{p.name} · {p.email}</p>
      {p.channel_url && <a href={p.channel_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-sky-300/70 hover:text-sky-300 truncate block max-w-[240px]">{p.channel_url}</a>}
    </td>
    <td className="py-2.5 pr-4"><span data-testid={`ref-status-${p.promoter_id}`} className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${STATUS_STYLE[p.status]}`}>{p.status}</span></td>
    <td className="py-2.5 pr-4 text-right font-mono text-amber">{fmtNum(p.month_uniques)}</td>
    <td className="py-2.5 pr-4 text-right font-mono text-white/55">{fmtNum(p.uniques)}<span className="text-white/30"> / {fmtNum(p.pageviews)}</span></td>
    <td className="py-2.5 pr-4 text-right font-mono text-white/60">{fmtNum(p.affiliate_clicks)}</td>
    <td className="py-2.5 pr-4 text-right font-mono text-emerald-300">
      ${p.estimated_payout.toFixed(2)}
      {p.next_tier && <p className="text-[9px] text-white/30">{fmtNum(p.next_tier_at - p.month_uniques)} to {p.next_tier}</p>}
    </td>
    <td className="py-2.5 pr-4 font-mono text-[11px] text-white/40">{fmtDate(p.last_visit)}</td>
    <td className="py-2.5 text-right whitespace-nowrap">
      {p.status !== "approved" && (
        <button data-testid={`ref-approve-${p.promoter_id}`} onClick={() => onStatus(p.promoter_id, "approved")} title="Approve" className="mr-1 border border-emerald-400/40 p-1.5 text-emerald-300 hover:bg-emerald-400/10 transition-colors"><Check size={13} weight="bold" /></button>
      )}
      {p.status !== "rejected" && (
        <button data-testid={`ref-reject-${p.promoter_id}`} onClick={() => onStatus(p.promoter_id, "rejected")} title="Reject" className="border border-crimson/40 p-1.5 text-crimson hover:bg-crimson/10 transition-colors"><X size={13} weight="bold" /></button>
      )}
    </td>
  </tr>
);

const HEADERS = ["Promoter", "Status", "Uniques (mo)", "Lifetime u / views", "Aff. clicks", "Est. payout (mo)", "Last visit", ""];

export const ReferralPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const headers = { "X-Admin-Token": token };

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/promoters`, { headers });
      setData(d);
      setTiers(d.tiers);
      setError("");
    } catch (e) {
      setData(null);
      setError(e.response?.status === 401 ? "Invalid or missing admin token." : "Could not load referral data.");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/promoters/${id}`, { status }, { headers });
      toast.success(`@${id} ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const saveTiers = async () => {
    try {
      await axios.put(`${API}/admin/referral-settings`, { tiers }, { headers });
      toast.success("Commission tiers saved");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save tiers");
    }
  };

  return (
    <section data-testid="referral-panel" className="border border-violet-300/25 bg-gradient-to-br from-violet-300/[0.05] to-transparent p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <UsersThree size={26} weight="duotone" className="text-violet-200" />
          <h2 className="font-serif text-2xl tracking-tight text-[#EDEDED]">Referral Traffic</h2>
          {data?.month_start && <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">since {fmtDate(data.month_start)}</span>}
        </div>
        <button data-testid="ref-refresh" onClick={load} disabled={loading} className="flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/60 hover:text-violet-200 transition-colors disabled:opacity-40">
          <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      {error && <p data-testid="ref-error" className="mb-4 font-mono text-[12px] text-crimson">{error}</p>}
      {data && <Totals t={data.totals} />}
      {data && <TierEditor tiers={tiers} setTiers={setTiers} onSave={saveTiers} />}
      {data?.promoters?.length ? (
        <div className="overflow-x-auto">
          <table data-testid="ref-table" className="w-full text-left border-collapse">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 border-b border-white/10">
                {HEADERS.map((h, i) => (
                  <th key={h || i} className={`py-2 pr-4 font-normal ${i >= 2 && i <= 5 ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{data.promoters.map((p) => <Row key={p.promoter_id} p={p} onStatus={setStatus} />)}</tbody>
          </table>
        </div>
      ) : !error && (
        <p data-testid="ref-empty" className="font-mono text-[12px] text-white/35">No partner applications yet. Share /partners to recruit promoters.</p>
      )}
    </section>
  );
};
